// TanStack Query hooks. Workspace ID is resolved from the route param and
// passed in explicitly so cache keys stay clean.

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";

import { apiRequest } from "./api";
import type {
  AccountCreate,
  AccountLink,
  AccountListPage,
  AccountRead,
  AccountUpdate,
  ActivityCreate,
  ActivityListPage,
  ActivityRead,
  ActivityStatus,
  AttachableEntity,
  ContactAccountCreate,
  ContactAccountUpdate,
  ContactCreate,
  ContactLink,
  ContactListItem,
  ContactListPage,
  ContactRead,
  ContactUpdate,
  CustomFieldDefinitionCreate,
  CustomFieldDefinitionRead,
  CustomFieldDefinitionUpdate,
  CustomFieldEntityType,
  CustomObjectDefinitionCreate,
  CustomObjectDefinitionList,
  CustomObjectDefinitionRead,
  CustomObjectDefinitionUpdate,
  AgentActionPage,
  AgentActionParams,
  AgentActionRead,
  AgentList,
  AgentRead,
  AgentUpdate,
  AIModelCatalogResponse,
  BriefResponse,
  BulkApproveResponse,
  DraftEmailRequest,
  DraftEmailResponse,
  InvokeBuiltinResponse,
  LeadScoreResponse,
  QaResponse,
  AuditLogPage,
  AuditLogParams,
  WorkspaceAISettingsResponse,
  WorkspaceAISettingsUpdate,
  CustomRecordCreate,
  CustomRecordListPage,
  CustomRecordRead,
  CustomRecordUpdate,
  DealCreate,
  DealLineItemCreate,
  DealLineItemList,
  DealLineItemRead,
  DealLineItemUpdate,
  DealListPage,
  DealListParams,
  DealRead,
  DashboardResponse,
  DealUpdate,
  Me,
  NoteCreate,
  NoteUpdate,
  PipelineUpdate,
  SavedViewCreate,
  SavedViewEntityType,
  SavedViewList,
  SavedViewRead,
  SavedViewUpdate,
  StageUpdate,
  ActivityUpdate,
  WorkspaceRoleRead,
  NoteListPage,
  NoteRead,
  PipelineCreate,
  PipelineRead,
  ProductCreate,
  ProductListPage,
  ProductListParams,
  ProductRead,
  ProductUpdate,
  SearchResults,
  StageCreate,
  StageRead,
  SubscriptionListResponse,
  WorkspaceRead,
  WorkspaceUser,
  WorkspaceUserListResponse,
} from "./types";

// ── Accounts ─────────────────────────────────────────────────────────────────

export type AccountListParams = {
  q?: string;
  kind?: string;
  lifecycle_stage?: string;
  tag?: string;
  owner_user_id?: string;
  parent_account_id?: string;
  cursor?: string;
  limit?: number;
};

export function useAccounts(wsId: () => string, params: () => AccountListParams) {
  return useQuery(() => ({
    queryKey: ["accounts", wsId(), params()],
    queryFn: () =>
      apiRequest<AccountListPage>(`/v1/workspaces/${wsId()}/accounts`, {
        query: params(),
      }),
  }));
}

// Cursor-paginated variant used by the list page. Keep the one-shot
// `useAccounts` above for the AccountPicker autocomplete where load-more
// makes no sense.
export function useAccountsInfinite(
  wsId: () => string,
  params: () => Omit<AccountListParams, "cursor">,
) {
  return useInfiniteQuery(() => ({
    queryKey: ["accounts-infinite", wsId(), params()],
    queryFn: ({ pageParam }) =>
      apiRequest<AccountListPage>(`/v1/workspaces/${wsId()}/accounts`, {
        query: { ...params(), cursor: (pageParam as string) || undefined },
      }),
    initialPageParam: "" as string,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  }));
}

export function useAccount(wsId: () => string, accId: () => string) {
  return useQuery(() => ({
    queryKey: ["accounts", wsId(), accId()],
    queryFn: () => apiRequest<AccountRead>(`/v1/workspaces/${wsId()}/accounts/${accId()}`),
    enabled: !!accId(),
  }));
}

export function useCreateAccount(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: AccountCreate) =>
      apiRequest<AccountRead>(`/v1/workspaces/${wsId()}/accounts`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", wsId()] });
    },
  }));
}

export function useMergeAccount(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { doomedAccId: string; intoAccountId: string }) =>
      apiRequest<AccountRead>(`/v1/workspaces/${wsId()}/accounts/${args.doomedAccId}:merge`, {
        method: "POST",
        body: { into_account_id: args.intoAccountId },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", wsId()] });
      qc.invalidateQueries({ queryKey: ["accounts-infinite", wsId()] });
    },
  }));
}

export function useArchiveAccount(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { accId: string; version: number }) =>
      apiRequest<AccountRead>(`/v1/workspaces/${wsId()}/accounts/${args.accId}:archive`, {
        method: "POST",
        ifMatch: args.version,
      }),
    onSuccess: (acc) => {
      qc.invalidateQueries({ queryKey: ["accounts", wsId()] });
      qc.setQueryData(["accounts", wsId(), acc.id], acc);
    },
  }));
}

export function useUpdateAccount(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { accId: string; body: AccountUpdate; version: number }) =>
      apiRequest<AccountRead>(`/v1/workspaces/${wsId()}/accounts/${args.accId}`, {
        method: "PATCH",
        body: args.body,
        ifMatch: args.version,
      }),
    onSuccess: (acc) => {
      qc.invalidateQueries({ queryKey: ["accounts", wsId()] });
      qc.setQueryData(["accounts", wsId(), acc.id], acc);
    },
  }));
}

// ── Contacts ─────────────────────────────────────────────────────────────────

export type ContactListParams = {
  q?: string;
  lifecycle_stage?: string;
  primary_account_id?: string;
  do_not_contact?: boolean;
  tag?: string;
  owner_user_id?: string;
  lead_score_min?: number;
  lead_score_max?: number;
  unscored_only?: boolean;
  cursor?: string;
  limit?: number;
};

export function useContactsInfinite(
  wsId: () => string,
  params: () => Omit<ContactListParams, "cursor">,
) {
  return useInfiniteQuery(() => ({
    queryKey: ["contacts-infinite", wsId(), params()],
    queryFn: ({ pageParam }) =>
      apiRequest<ContactListPage>(`/v1/workspaces/${wsId()}/contacts`, {
        query: { ...params(), cursor: (pageParam as string) || undefined },
      }),
    initialPageParam: "" as string,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  }));
}

export function useContacts(wsId: () => string, params: () => ContactListParams) {
  return useQuery(() => ({
    queryKey: ["contacts", wsId(), params()],
    queryFn: () =>
      apiRequest<ContactListPage>(`/v1/workspaces/${wsId()}/contacts`, {
        query: params(),
      }),
  }));
}

export function useContact(wsId: () => string, conId: () => string) {
  return useQuery(() => ({
    queryKey: ["contacts", wsId(), conId()],
    queryFn: () => apiRequest<ContactRead>(`/v1/workspaces/${wsId()}/contacts/${conId()}`),
    enabled: !!conId(),
  }));
}

export function useCreateContact(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: ContactCreate) =>
      apiRequest<ContactRead>(`/v1/workspaces/${wsId()}/contacts`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts", wsId()] });
    },
  }));
}

export function useMergeContact(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: {
      doomedConId: string;
      intoContactId: string;
      keepLinkedUserId?: string | null;
    }) =>
      apiRequest<ContactRead>(`/v1/workspaces/${wsId()}/contacts/${args.doomedConId}:merge`, {
        method: "POST",
        body: {
          into_contact_id: args.intoContactId,
          keep_linked_user_id: args.keepLinkedUserId ?? undefined,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contacts", wsId()] });
      qc.invalidateQueries({ queryKey: ["contacts-infinite", wsId()] });
    },
  }));
}

export function useArchiveContact(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { conId: string; version: number }) =>
      apiRequest<ContactRead>(`/v1/workspaces/${wsId()}/contacts/${args.conId}:archive`, {
        method: "POST",
        ifMatch: args.version,
      }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["contacts", wsId()] });
      qc.setQueryData(["contacts", wsId(), c.id], c);
    },
  }));
}

export function useUpdateContact(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { conId: string; body: ContactUpdate; version: number }) =>
      apiRequest<ContactRead>(`/v1/workspaces/${wsId()}/contacts/${args.conId}`, {
        method: "PATCH",
        body: args.body,
        ifMatch: args.version,
      }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["contacts", wsId()] });
      qc.setQueryData(["contacts", wsId(), c.id], c);
    },
  }));
}

// ── Search ───────────────────────────────────────────────────────────────────

export function useSearch(wsId: () => string, q: () => string, limit?: () => number | undefined) {
  return useQuery(() => ({
    queryKey: ["search", wsId(), q(), limit?.() ?? null],
    queryFn: () =>
      apiRequest<SearchResults>(`/v1/workspaces/${wsId()}/search`, {
        query: { q: q(), ...(limit?.() ? { limit: limit() } : {}) },
      }),
    enabled: q().length >= 1,
  }));
}

// ── Notes ────────────────────────────────────────────────────────────────────

export function useNotes(
  wsId: () => string,
  attachedTo: () => { entity_type: AttachableEntity; entity_id: string } | null,
) {
  return useQuery(() => ({
    queryKey: ["notes", wsId(), attachedTo()],
    queryFn: () => {
      const a = attachedTo();
      return apiRequest<NoteListPage>(`/v1/workspaces/${wsId()}/notes`, {
        query: a
          ? {
              attached_to_entity_type: a.entity_type,
              attached_to_entity_id: a.entity_id,
              limit: 100,
            }
          : { limit: 100 },
      });
    },
    enabled: !!attachedTo(),
  }));
}

export function useCreateNote(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: NoteCreate) =>
      apiRequest<NoteRead>(`/v1/workspaces/${wsId()}/notes`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notes", wsId()] });
    },
  }));
}

export function useUpdateNote(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { noteId: string; body: NoteUpdate; version: number }) =>
      apiRequest<NoteRead>(`/v1/workspaces/${wsId()}/notes/${args.noteId}`, {
        method: "PATCH",
        body: args.body,
        ifMatch: args.version,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notes", wsId()] });
    },
  }));
}

export function usePinNote(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { noteId: string; pinned: boolean; version: number }) =>
      apiRequest<NoteRead>(
        `/v1/workspaces/${wsId()}/notes/${args.noteId}:${args.pinned ? "pin" : "unpin"}`,
        { method: "POST", ifMatch: args.version },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notes", wsId()] });
    },
  }));
}

export function useArchiveNote(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { noteId: string; version: number }) =>
      apiRequest<NoteRead>(`/v1/workspaces/${wsId()}/notes/${args.noteId}:archive`, {
        method: "POST",
        ifMatch: args.version,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notes", wsId()] });
    },
  }));
}

// ── Activities ───────────────────────────────────────────────────────────────

export type ActivityListParams = {
  primary_entity_type?: AttachableEntity;
  primary_entity_id?: string;
  kind?: string;
  status?: ActivityStatus;
  owner_user_id?: string;
  cursor?: string;
  limit?: number;
};

export function useActivities(wsId: () => string, params: () => ActivityListParams) {
  return useQuery(() => ({
    queryKey: ["activities", wsId(), params()],
    queryFn: () =>
      apiRequest<ActivityListPage>(`/v1/workspaces/${wsId()}/activities`, {
        query: params(),
      }),
  }));
}

export function useCreateActivity(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: ActivityCreate) =>
      apiRequest<ActivityRead>(`/v1/workspaces/${wsId()}/activities`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities", wsId()] });
    },
  }));
}

export function useUpdateActivity(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { activityId: string; body: ActivityUpdate; version: number }) =>
      apiRequest<ActivityRead>(`/v1/workspaces/${wsId()}/activities/${args.activityId}`, {
        method: "PATCH",
        body: args.body,
        ifMatch: args.version,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities", wsId()] });
    },
  }));
}

export function useCompleteTask(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { activityId: string; version: number }) =>
      apiRequest<ActivityRead>(`/v1/workspaces/${wsId()}/activities/${args.activityId}:complete`, {
        method: "POST",
        ifMatch: args.version,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities", wsId()] });
    },
  }));
}

export function useRescheduleTask(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { activityId: string; due_date: string; version: number }) =>
      apiRequest<ActivityRead>(
        `/v1/workspaces/${wsId()}/activities/${args.activityId}:reschedule`,
        { method: "POST", body: { due_date: args.due_date }, ifMatch: args.version },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["activities", wsId()] });
    },
  }));
}

// ── Custom fields ────────────────────────────────────────────────────────────

export function useCustomFields(
  wsId: () => string,
  entityType: () => CustomFieldEntityType | undefined,
) {
  return useQuery(() => ({
    queryKey: ["custom-fields", wsId(), entityType()],
    queryFn: () =>
      apiRequest<CustomFieldDefinitionRead[]>(`/v1/workspaces/${wsId()}/custom-fields`, {
        query: entityType() ? { entity_type: entityType() } : {},
      }),
  }));
}

export function useCustomFieldsForObject(wsId: () => string, codId: () => string | undefined) {
  return useQuery(() => ({
    queryKey: ["custom-fields", wsId(), "object", codId()],
    queryFn: () =>
      apiRequest<CustomFieldDefinitionRead[]>(`/v1/workspaces/${wsId()}/custom-fields`, {
        query: { custom_object_definition_id: codId() },
      }),
    enabled: !!codId(),
  }));
}

export function useCreateCustomField(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: CustomFieldDefinitionCreate) =>
      apiRequest<CustomFieldDefinitionRead>(`/v1/workspaces/${wsId()}/custom-fields`, {
        method: "POST",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-fields", wsId()] }),
  }));
}

export function useUpdateCustomField(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: {
      cfdId: string;
      body: CustomFieldDefinitionUpdate;
      version: number;
    }) =>
      apiRequest<CustomFieldDefinitionRead>(
        `/v1/workspaces/${wsId()}/custom-fields/${args.cfdId}`,
        { method: "PATCH", body: args.body, ifMatch: args.version },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-fields", wsId()] }),
  }));
}

export function useArchiveCustomField(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { cfdId: string; version: number }) =>
      apiRequest<CustomFieldDefinitionRead>(
        `/v1/workspaces/${wsId()}/custom-fields/${args.cfdId}:archive`,
        { method: "POST", ifMatch: args.version },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-fields", wsId()] }),
  }));
}

// ── Custom Objects (definitions) ─────────────────────────────────────────────

export function useCustomObjects(wsId: () => string, opts?: () => { include_archived?: boolean }) {
  return useQuery(() => ({
    queryKey: ["custom-objects", wsId(), opts?.()],
    queryFn: () =>
      apiRequest<CustomObjectDefinitionList>(`/v1/workspaces/${wsId()}/custom_objects`, {
        query: opts?.(),
      }),
  }));
}

export function useCreateCustomObject(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: CustomObjectDefinitionCreate) =>
      apiRequest<CustomObjectDefinitionRead>(`/v1/workspaces/${wsId()}/custom_objects`, {
        method: "POST",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-objects", wsId()] }),
  }));
}

export function useUpdateCustomObject(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: {
      codId: string;
      body: CustomObjectDefinitionUpdate;
      version: number;
    }) =>
      apiRequest<CustomObjectDefinitionRead>(
        `/v1/workspaces/${wsId()}/custom_objects/${args.codId}`,
        { method: "PATCH", body: args.body, ifMatch: args.version },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-objects", wsId()] }),
  }));
}

export function useArchiveCustomObject(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { codId: string; version: number }) =>
      apiRequest<CustomObjectDefinitionRead>(
        `/v1/workspaces/${wsId()}/custom_objects/${args.codId}:archive`,
        { method: "POST", ifMatch: args.version },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-objects", wsId()] }),
  }));
}

// ── Pipelines + Stages ───────────────────────────────────────────────────────

export function usePipelines(wsId: () => string) {
  return useQuery(() => ({
    queryKey: ["pipelines", wsId()],
    queryFn: () => apiRequest<PipelineRead[]>(`/v1/workspaces/${wsId()}/pipelines`),
  }));
}

export function useStages(wsId: () => string, pipelineId: () => string | undefined) {
  return useQuery(() => ({
    queryKey: ["stages", wsId(), pipelineId()],
    queryFn: () =>
      apiRequest<StageRead[]>(`/v1/workspaces/${wsId()}/pipelines/${pipelineId()}/stages`),
    enabled: !!pipelineId(),
  }));
}

export function useCreatePipeline(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: PipelineCreate) =>
      apiRequest<PipelineRead>(`/v1/workspaces/${wsId()}/pipelines`, {
        method: "POST",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipelines", wsId()] }),
  }));
}

export function useCreateStage(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { pipelineId: string; body: StageCreate }) =>
      apiRequest<StageRead>(`/v1/workspaces/${wsId()}/pipelines/${args.pipelineId}/stages`, {
        method: "POST",
        body: args.body,
      }),
    onSuccess: (_stage, args) =>
      qc.invalidateQueries({ queryKey: ["stages", wsId(), args.pipelineId] }),
  }));
}

export function useReorderStages(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { pipelineId: string; stageIds: string[] }) =>
      apiRequest<StageRead[]>(
        `/v1/workspaces/${wsId()}/pipelines/${args.pipelineId}/stages:reorder`,
        { method: "POST", body: { stage_ids: args.stageIds } },
      ),
    onSuccess: (_rows, args) =>
      qc.invalidateQueries({ queryKey: ["stages", wsId(), args.pipelineId] }),
  }));
}

export function useUpdatePipeline(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { pipelineId: string; body: PipelineUpdate; version: number }) =>
      apiRequest<PipelineRead>(`/v1/workspaces/${wsId()}/pipelines/${args.pipelineId}`, {
        method: "PATCH",
        body: args.body,
        ifMatch: args.version,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipelines", wsId()] }),
  }));
}

export function useArchivePipeline(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { pipelineId: string; version: number }) =>
      apiRequest<PipelineRead>(`/v1/workspaces/${wsId()}/pipelines/${args.pipelineId}:archive`, {
        method: "POST",
        ifMatch: args.version,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipelines", wsId()] }),
  }));
}

export function useMakeDefaultPipeline(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { pipelineId: string; version: number }) =>
      apiRequest<PipelineRead>(
        `/v1/workspaces/${wsId()}/pipelines/${args.pipelineId}:make_default`,
        { method: "POST", ifMatch: args.version },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipelines", wsId()] }),
  }));
}

export function useUpdateStage(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: {
      pipelineId: string;
      stageId: string;
      body: StageUpdate;
      version: number;
    }) =>
      apiRequest<StageRead>(
        `/v1/workspaces/${wsId()}/pipelines/${args.pipelineId}/stages/${args.stageId}`,
        { method: "PATCH", body: args.body, ifMatch: args.version },
      ),
    onSuccess: (_s, args) =>
      qc.invalidateQueries({ queryKey: ["stages", wsId(), args.pipelineId] }),
  }));
}

export function useArchiveStage(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { pipelineId: string; stageId: string; version: number }) =>
      apiRequest<StageRead>(
        `/v1/workspaces/${wsId()}/pipelines/${args.pipelineId}/stages/${args.stageId}:archive`,
        { method: "POST", ifMatch: args.version },
      ),
    onSuccess: (_s, args) =>
      qc.invalidateQueries({ queryKey: ["stages", wsId(), args.pipelineId] }),
  }));
}

// ── Deals ────────────────────────────────────────────────────────────────────

export function useDeals(wsId: () => string, params: () => DealListParams) {
  return useQuery(() => ({
    queryKey: ["deals", wsId(), params()],
    queryFn: () =>
      apiRequest<DealListPage>(`/v1/workspaces/${wsId()}/deals`, {
        query: params(),
      }),
  }));
}

export function useDeal(wsId: () => string, dealId: () => string) {
  return useQuery(() => ({
    queryKey: ["deals", wsId(), dealId()],
    queryFn: () => apiRequest<DealRead>(`/v1/workspaces/${wsId()}/deals/${dealId()}`),
    enabled: !!dealId(),
  }));
}

export function useCreateDeal(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: DealCreate) =>
      apiRequest<DealRead>(`/v1/workspaces/${wsId()}/deals`, {
        method: "POST",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deals", wsId()] }),
  }));
}

export function useUpdateDeal(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { dealId: string; body: DealUpdate; version: number }) =>
      apiRequest<DealRead>(`/v1/workspaces/${wsId()}/deals/${args.dealId}`, {
        method: "PATCH",
        body: args.body,
        ifMatch: args.version,
      }),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["deals", wsId()] });
      qc.setQueryData(["deals", wsId(), deal.id], deal);
    },
  }));
}

export function useArchiveDeal(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { dealId: string; version: number }) =>
      apiRequest<DealRead>(`/v1/workspaces/${wsId()}/deals/${args.dealId}:archive`, {
        method: "POST",
        ifMatch: args.version,
      }),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["deals", wsId()] });
      qc.setQueryData(["deals", wsId(), deal.id], deal);
    },
  }));
}

export function useMoveDealToStage(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { dealId: string; stage_id: string; version: number }) =>
      apiRequest<DealRead>(`/v1/workspaces/${wsId()}/deals/${args.dealId}:move_to_stage`, {
        method: "POST",
        body: { stage_id: args.stage_id },
        ifMatch: args.version,
      }),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["deals", wsId()] });
      qc.setQueryData(["deals", wsId(), deal.id], deal);
    },
  }));
}

export function useMarkDealWon(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { dealId: string; version: number }) =>
      apiRequest<DealRead>(`/v1/workspaces/${wsId()}/deals/${args.dealId}:mark_won`, {
        method: "POST",
        ifMatch: args.version,
      }),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["deals", wsId()] });
      qc.setQueryData(["deals", wsId(), deal.id], deal);
    },
  }));
}

export function useMarkDealLost(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: {
      dealId: string;
      version: number;
      lost_reason: string;
      lost_reason_detail?: string;
    }) =>
      apiRequest<DealRead>(`/v1/workspaces/${wsId()}/deals/${args.dealId}:mark_lost`, {
        method: "POST",
        body: {
          lost_reason: args.lost_reason,
          lost_reason_detail: args.lost_reason_detail,
        },
        ifMatch: args.version,
      }),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["deals", wsId()] });
      qc.setQueryData(["deals", wsId(), deal.id], deal);
    },
  }));
}

export function useAbandonDeal(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { dealId: string; version: number }) =>
      apiRequest<DealRead>(`/v1/workspaces/${wsId()}/deals/${args.dealId}:abandon`, {
        method: "POST",
        ifMatch: args.version,
      }),
    onSuccess: (deal) => {
      qc.invalidateQueries({ queryKey: ["deals", wsId()] });
      qc.setQueryData(["deals", wsId(), deal.id], deal);
    },
  }));
}

// ── Workspace + Subscriptions ────────────────────────────────────────────────

export function useWorkspace(wsId: () => string) {
  return useQuery(() => ({
    queryKey: ["workspace", wsId()],
    queryFn: () => apiRequest<WorkspaceRead>(`/v1/workspaces/${wsId()}`),
  }));
}

export function useSubscriptions(wsId: () => string) {
  return useQuery(() => ({
    queryKey: ["subscriptions", wsId()],
    queryFn: () => apiRequest<SubscriptionListResponse>(`/v1/workspaces/${wsId()}/subscriptions`),
  }));
}

// ── ScaiControl plan catalogue + actions ─────────────────────────────────────

import type {
  BulkActionRequest,
  BulkActionResponse,
  CancelSubscriptionRequest,
  ChangePlanRequest,
  PlanCatalogueResponse,
  SubscriptionActionResponse,
  SubscriptionHistoryResponse,
} from "./types";

export function usePlanCatalogue(wsId: () => string, enabled: () => boolean = () => true) {
  return useQuery(() => ({
    queryKey: ["scaicontrol-plans", wsId()],
    queryFn: () => apiRequest<PlanCatalogueResponse>(`/v1/workspaces/${wsId()}/scaicontrol/plans`),
    enabled: enabled(),
    // Backend cache is 15min; client cache 5min keeps the dialog snappy
    // without serving truly stale plan names.
    staleTime: 5 * 60_000,
  }));
}

function _invalidateSubs(qc: ReturnType<typeof useQueryClient>, wsId: string) {
  qc.invalidateQueries({ queryKey: ["subscriptions", wsId] });
}

export function useChangeSubscriptionPlan(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: ({ subId, body }: { subId: string; body: ChangePlanRequest }) =>
      apiRequest<SubscriptionActionResponse>(
        `/v1/workspaces/${wsId()}/subscriptions/${subId}:change_plan`,
        { method: "POST", body },
      ),
    onSuccess: () => _invalidateSubs(qc, wsId()),
  }));
}

export function useCancelSubscription(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: ({ subId, body }: { subId: string; body: CancelSubscriptionRequest }) =>
      apiRequest<SubscriptionActionResponse>(
        `/v1/workspaces/${wsId()}/subscriptions/${subId}:cancel`,
        { method: "POST", body },
      ),
    onSuccess: () => _invalidateSubs(qc, wsId()),
  }));
}

export function useSuspendSubscription(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: ({ subId }: { subId: string }) =>
      apiRequest<SubscriptionActionResponse>(
        `/v1/workspaces/${wsId()}/subscriptions/${subId}:suspend`,
        { method: "POST" },
      ),
    onSuccess: () => _invalidateSubs(qc, wsId()),
  }));
}

export function useResumeSubscription(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: ({ subId }: { subId: string }) =>
      apiRequest<SubscriptionActionResponse>(
        `/v1/workspaces/${wsId()}/subscriptions/${subId}:resume`,
        { method: "POST" },
      ),
    onSuccess: () => _invalidateSubs(qc, wsId()),
  }));
}

export function useBulkSubscriptionAction(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: BulkActionRequest) =>
      apiRequest<BulkActionResponse>(
        `/v1/workspaces/${wsId()}/subscriptions:bulk_action`,
        { method: "POST", body },
      ),
    onSuccess: () => _invalidateSubs(qc, wsId()),
  }));
}

export function useSubscriptionHistory(
  wsId: () => string,
  subId: () => string,
  enabled: () => boolean = () => true,
) {
  return useQuery(() => ({
    queryKey: ["subscription-history", wsId(), subId()],
    queryFn: () =>
      apiRequest<SubscriptionHistoryResponse>(
        `/v1/workspaces/${wsId()}/subscriptions/${subId()}/history`,
      ),
    enabled: enabled() && !!subId(),
    // Short cache — history changes whenever someone clicks an action
    // in this panel, so we don't want a stale view immediately after
    // a plan change submits.
    staleTime: 30_000,
  }));
}

// ── Saved views ──────────────────────────────────────────────────────────────

export function useSavedViews(
  wsId: () => string,
  entityType: () => SavedViewEntityType,
  customObjectDefinitionId?: () => string | null,
) {
  return useQuery(() => ({
    queryKey: ["saved-views", wsId(), entityType(), customObjectDefinitionId?.() ?? null],
    queryFn: () =>
      apiRequest<SavedViewList>(`/v1/workspaces/${wsId()}/saved-views`, {
        query: {
          entity_type: entityType(),
          ...(customObjectDefinitionId?.()
            ? { custom_object_definition_id: customObjectDefinitionId() }
            : {}),
        },
      }),
    staleTime: 60_000,
  }));
}

export function useCreateSavedView(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: SavedViewCreate) =>
      apiRequest<SavedViewRead>(`/v1/workspaces/${wsId()}/saved-views`, {
        method: "POST",
        body,
      }),
    onSuccess: (sv) => qc.invalidateQueries({ queryKey: ["saved-views", wsId(), sv.entity_type] }),
  }));
}

export function useUpdateSavedView(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { svId: string; body: SavedViewUpdate; version: number }) =>
      apiRequest<SavedViewRead>(`/v1/workspaces/${wsId()}/saved-views/${args.svId}`, {
        method: "PATCH",
        body: args.body,
        ifMatch: args.version,
      }),
    onSuccess: (sv) => qc.invalidateQueries({ queryKey: ["saved-views", wsId(), sv.entity_type] }),
  }));
}

export function useDeleteSavedView(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { svId: string }) =>
      apiRequest<void>(`/v1/workspaces/${wsId()}/saved-views/${args.svId}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["saved-views", wsId()] }),
  }));
}

export function useMakeDefaultSavedView(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { svId: string }) =>
      apiRequest<SavedViewRead>(`/v1/workspaces/${wsId()}/saved-views/${args.svId}:make_default`, {
        method: "POST",
      }),
    onSuccess: (sv) => qc.invalidateQueries({ queryKey: ["saved-views", wsId(), sv.entity_type] }),
  }));
}

// ── Dashboard ────────────────────────────────────────────────────────────────

export function useDashboard(wsId: () => string) {
  return useQuery(() => ({
    queryKey: ["dashboard", wsId()],
    queryFn: () => apiRequest<DashboardResponse>(`/v1/workspaces/${wsId()}/dashboard`),
    // 30s is short enough to feel live, long enough to avoid hammering
    // while navigating.
    staleTime: 30_000,
  }));
}

// ── Me ───────────────────────────────────────────────────────────────────────

export function useMe() {
  return useQuery(() => ({
    queryKey: ["me"],
    queryFn: () => apiRequest<Me>(`/v1/me`),
    staleTime: 5 * 60_000,
  }));
}

export function useMyWorkspaces() {
  // Imported lazily here so the top-level types-block stays untouched.
  type Resp = import("./types").MyWorkspacesResponse;
  return useQuery(() => ({
    queryKey: ["me", "workspaces"],
    queryFn: () => apiRequest<Resp>(`/v1/me/workspaces`),
    staleTime: 5 * 60_000,
  }));
}

// ── Partner AI settings ─────────────────────────────────────────────────────

import type { PartnerAISettingsRead, PartnerAISettingsUpdate } from "./types";

export function usePartnerAISettings(partnerId: () => string) {
  return useQuery(() => ({
    queryKey: ["partner-ai-settings", partnerId()],
    queryFn: () =>
      apiRequest<PartnerAISettingsRead | null>(`/v1/partners/${partnerId()}/ai-settings`),
    enabled: !!partnerId(),
  }));
}

export function useUpdatePartnerAISettings(partnerId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: PartnerAISettingsUpdate) =>
      apiRequest<PartnerAISettingsRead>(`/v1/partners/${partnerId()}/ai-settings`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["partner-ai-settings", partnerId()] }),
  }));
}

export function useDeletePartnerAISettings(partnerId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: () =>
      apiRequest<void>(`/v1/partners/${partnerId()}/ai-settings`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["partner-ai-settings", partnerId()] }),
  }));
}

// ── Workspace roles ──────────────────────────────────────────────────────────

export function useWorkspaceRoles(wsId: () => string) {
  return useQuery(() => ({
    queryKey: ["workspace-roles", wsId()],
    queryFn: () => apiRequest<WorkspaceRoleRead[]>(`/v1/workspaces/${wsId()}/roles`),
    staleTime: 5 * 60_000,
  }));
}

// ── Workspace users (for the UserPicker / owner assignment) ──────────────────

export function useWorkspaceUsers(wsId: () => string, q: () => string) {
  return useQuery(() => ({
    queryKey: ["workspace-users", wsId(), q()],
    queryFn: () =>
      apiRequest<WorkspaceUserListResponse>(`/v1/workspaces/${wsId()}/users`, {
        query: { q: q() || undefined },
      }),
    staleTime: 60_000,
  }));
}

// Single user — only the chip label needs this, so it's keyed independently
// from the search list and cached for ~5 min (names rarely change).
export function useWorkspaceUser(wsId: () => string, userId: () => string | null) {
  return useQuery(() => ({
    queryKey: ["workspace-user", wsId(), userId()],
    queryFn: () => apiRequest<WorkspaceUser>(`/v1/workspaces/${wsId()}/users/${userId()}`),
    enabled: !!userId(),
    staleTime: 5 * 60_000,
  }));
}

// ── Tag suggestions ──────────────────────────────────────────────────────────

export function useTagSuggestions(
  wsId: () => string,
  q: () => string,
  entityType: () => "account" | "contact" | "deal" | "all",
) {
  return useQuery(() => ({
    queryKey: ["tags", wsId(), entityType(), q()],
    queryFn: () =>
      apiRequest<{ items: string[] }>(`/v1/workspaces/${wsId()}/tags`, {
        query: {
          q: q() || undefined,
          entity_type: entityType(),
        },
      }),
    // Keep cache for a minute — tag set doesn't change often.
    staleTime: 60_000,
  }));
}

// ── Associations (Contact ↔ Account works_at) ────────────────────────────────

export function useContactAccounts(wsId: () => string, conId: () => string) {
  return useQuery(() => ({
    queryKey: ["contact-accounts", wsId(), conId()],
    queryFn: () =>
      apiRequest<{ items: AccountLink[] }>(`/v1/workspaces/${wsId()}/contacts/${conId()}/accounts`),
    enabled: !!conId(),
  }));
}

export function useAccountContacts(wsId: () => string, accId: () => string) {
  return useQuery(() => ({
    queryKey: ["account-contacts", wsId(), accId()],
    queryFn: () =>
      apiRequest<{ items: ContactLink[] }>(`/v1/workspaces/${wsId()}/accounts/${accId()}/contacts`),
    enabled: !!accId(),
  }));
}

// Invalidate both sides of a Contact↔Account association change.
function invalidateAssociationCaches(
  qc: ReturnType<typeof useQueryClient>,
  wsId: string,
  conId: string,
  accId?: string,
) {
  qc.invalidateQueries({ queryKey: ["contact-accounts", wsId, conId] });
  qc.invalidateQueries({ queryKey: ["contacts", wsId, conId] });
  if (accId) {
    qc.invalidateQueries({ queryKey: ["account-contacts", wsId, accId] });
  } else {
    qc.invalidateQueries({ queryKey: ["account-contacts", wsId] });
  }
}

export function useAddContactAccount(wsId: () => string, conId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: ContactAccountCreate) =>
      apiRequest<AccountLink>(`/v1/workspaces/${wsId()}/contacts/${conId()}/accounts`, {
        method: "POST",
        body,
      }),
    onSuccess: (link) => invalidateAssociationCaches(qc, wsId(), conId(), link.account_id),
  }));
}

export function useUpdateContactAccount(wsId: () => string, conId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { accId: string; body: ContactAccountUpdate }) =>
      apiRequest<AccountLink>(
        `/v1/workspaces/${wsId()}/contacts/${conId()}/accounts/${args.accId}`,
        { method: "PATCH", body: args.body },
      ),
    onSuccess: (link) => invalidateAssociationCaches(qc, wsId(), conId(), link.account_id),
  }));
}

export function useRemoveContactAccount(wsId: () => string, conId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { accId: string }) =>
      apiRequest<void>(`/v1/workspaces/${wsId()}/contacts/${conId()}/accounts/${args.accId}`, {
        method: "DELETE",
      }),
    onSuccess: (_, args) => invalidateAssociationCaches(qc, wsId(), conId(), args.accId),
  }));
}

// ── Products (catalogue) ─────────────────────────────────────────────────────

export function useProducts(wsId: () => string, params: () => ProductListParams) {
  return useQuery(() => ({
    queryKey: ["products", wsId(), params()],
    queryFn: () =>
      apiRequest<ProductListPage>(`/v1/workspaces/${wsId()}/products`, {
        query: params(),
      }),
  }));
}

export function useProduct(wsId: () => string, productId: () => string | null) {
  return useQuery(() => ({
    queryKey: ["products", wsId(), productId()],
    queryFn: () => apiRequest<ProductRead>(`/v1/workspaces/${wsId()}/products/${productId()}`),
    enabled: !!productId(),
  }));
}

export function useCreateProduct(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: ProductCreate) =>
      apiRequest<ProductRead>(`/v1/workspaces/${wsId()}/products`, {
        method: "POST",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products", wsId()] }),
  }));
}

export function useUpdateProduct(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { productId: string; body: ProductUpdate; version: number }) =>
      apiRequest<ProductRead>(`/v1/workspaces/${wsId()}/products/${args.productId}`, {
        method: "PATCH",
        body: args.body,
        ifMatch: args.version,
      }),
    onSuccess: (prd) => {
      qc.invalidateQueries({ queryKey: ["products", wsId()] });
      qc.setQueryData(["products", wsId(), prd.id], prd);
    },
  }));
}

export function useArchiveProduct(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { productId: string; version: number }) =>
      apiRequest<ProductRead>(`/v1/workspaces/${wsId()}/products/${args.productId}:archive`, {
        method: "POST",
        ifMatch: args.version,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products", wsId()] }),
  }));
}

// ── Deal line items ──────────────────────────────────────────────────────────

export function useDealLineItems(wsId: () => string, dealId: () => string) {
  return useQuery(() => ({
    queryKey: ["deals", wsId(), dealId(), "line_items"],
    queryFn: () =>
      apiRequest<DealLineItemList>(`/v1/workspaces/${wsId()}/deals/${dealId()}/line_items`),
    enabled: !!dealId(),
  }));
}

export function useAddDealLineItem(wsId: () => string, dealId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: DealLineItemCreate) =>
      apiRequest<DealLineItemRead>(`/v1/workspaces/${wsId()}/deals/${dealId()}/line_items`, {
        method: "POST",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deals", wsId(), dealId(), "line_items"] }),
  }));
}

export function useUpdateDealLineItem(wsId: () => string, dealId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: {
      lineItemId: string;
      body: DealLineItemUpdate;
      version: number;
    }) =>
      apiRequest<DealLineItemRead>(
        `/v1/workspaces/${wsId()}/deals/${dealId()}/line_items/${args.lineItemId}`,
        { method: "PATCH", body: args.body, ifMatch: args.version },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deals", wsId(), dealId(), "line_items"] }),
  }));
}

export function useRemoveDealLineItem(wsId: () => string, dealId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { lineItemId: string }) =>
      apiRequest<void>(`/v1/workspaces/${wsId()}/deals/${dealId()}/line_items/${args.lineItemId}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deals", wsId(), dealId(), "line_items"] }),
  }));
}

// ── Custom Records (data layer) ──────────────────────────────────────────────

export function useCustomRecords(
  wsId: () => string,
  codId: () => string,
  opts?: () => {
    q?: string;
    tag?: string;
    include_archived?: boolean;
  },
) {
  return useQuery(() => ({
    queryKey: ["custom-records", wsId(), codId(), opts?.()],
    queryFn: () =>
      apiRequest<CustomRecordListPage>(
        `/v1/workspaces/${wsId()}/custom_objects/${codId()}/records`,
        { query: opts?.() },
      ),
    enabled: !!codId(),
  }));
}

export function useCustomRecord(
  wsId: () => string,
  codId: () => string,
  recId: () => string | null,
) {
  return useQuery(() => ({
    queryKey: ["custom-records", wsId(), codId(), recId()],
    queryFn: () =>
      apiRequest<CustomRecordRead>(
        `/v1/workspaces/${wsId()}/custom_objects/${codId()}/records/${recId()}`,
      ),
    enabled: !!recId() && !!codId(),
  }));
}

export function useCreateCustomRecord(wsId: () => string, codId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: CustomRecordCreate) =>
      apiRequest<CustomRecordRead>(`/v1/workspaces/${wsId()}/custom_objects/${codId()}/records`, {
        method: "POST",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-records", wsId(), codId()] }),
  }));
}

export function useUpdateCustomRecord(wsId: () => string, codId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: {
      recId: string;
      body: CustomRecordUpdate;
      version: number;
    }) =>
      apiRequest<CustomRecordRead>(
        `/v1/workspaces/${wsId()}/custom_objects/${codId()}/records/${args.recId}`,
        { method: "PATCH", body: args.body, ifMatch: args.version },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-records", wsId(), codId()] }),
  }));
}

export function useArchiveCustomRecord(wsId: () => string, codId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { recId: string; version: number }) =>
      apiRequest<CustomRecordRead>(
        `/v1/workspaces/${wsId()}/custom_objects/${codId()}/records/${args.recId}:archive`,
        { method: "POST", ifMatch: args.version },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["custom-records", wsId(), codId()] }),
  }));
}

// ── Audit log (admin-only read) ──────────────────────────────────────────────

export function useAuditLog(wsId: () => string, params: () => AuditLogParams) {
  return useQuery(() => ({
    queryKey: ["audit-log", wsId(), params()],
    queryFn: () =>
      apiRequest<AuditLogPage>(`/v1/workspaces/${wsId()}/audit-log`, {
        query: params(),
      }),
  }));
}

// ── Workspace AI settings ────────────────────────────────────────────────────

export function useAiSettings(wsId: () => string) {
  return useQuery(() => ({
    queryKey: ["ai-settings", wsId()],
    queryFn: () => apiRequest<WorkspaceAISettingsResponse>(`/v1/workspaces/${wsId()}/ai-settings`),
  }));
}

export function useAiModelCatalog(wsId: () => string, enabled: () => boolean) {
  return useQuery(() => ({
    queryKey: ["ai-models", wsId()],
    queryFn: () =>
      apiRequest<AIModelCatalogResponse>(`/v1/workspaces/${wsId()}/ai-settings/models`),
    enabled: enabled(),
    // Catalog is admin-only + changes slowly upstream — stale time matches.
    staleTime: 5 * 60_000,
  }));
}

export function useUpdateAiSettings(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: WorkspaceAISettingsUpdate) =>
      apiRequest<WorkspaceAISettingsResponse>(`/v1/workspaces/${wsId()}/ai-settings`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai-settings", wsId()] }),
  }));
}

// ── Agent invocations ───────────────────────────────────────────────────────

export function useGenerateAccountBrief(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { accId: string }) =>
      apiRequest<BriefResponse>(`/v1/workspaces/${wsId()}/accounts/${args.accId}:generate_brief`, {
        method: "POST",
      }),
    // Each generation appends to history — refresh widgets so the new
    // row appears immediately.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent-actions", wsId()] }),
  }));
}

export type TopHotLeadsResponse = { items: ContactListItem[] };

export function useTopHotLeads(
  wsId: () => string,
  opts?: () => { n?: number; min_score?: number },
) {
  return useQuery(() => ({
    queryKey: ["contacts-top-hot", wsId(), opts?.() ?? {}],
    queryFn: () =>
      apiRequest<TopHotLeadsResponse>(`/v1/workspaces/${wsId()}/contacts:top_hot`, {
        query: { n: opts?.().n ?? 10, min_score: opts?.().min_score ?? 50 },
      }),
    staleTime: 60_000,
  }));
}

export function useAskWorkspace(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { question: string }) =>
      apiRequest<QaResponse>(`/v1/workspaces/${wsId()}/qa:ask`, {
        method: "POST",
        body: { question: args.question },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent-actions", wsId()] }),
  }));
}

export function useScoreLead(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { conId: string }) =>
      apiRequest<LeadScoreResponse>(`/v1/workspaces/${wsId()}/contacts/${args.conId}:score_lead`, {
        method: "POST",
      }),
    onSuccess: (_data, args) => {
      // Score is persisted on the contact, so refresh both the detail
      // view and any list pages (so the new score shows in sort/filter).
      qc.invalidateQueries({ queryKey: ["contacts", wsId(), args.conId] });
      qc.invalidateQueries({ queryKey: ["contacts", wsId()] });
      qc.invalidateQueries({ queryKey: ["contacts-infinite", wsId()] });
      qc.invalidateQueries({ queryKey: ["agent-actions", wsId()] });
    },
  }));
}

export function useDraftEmailForContact(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { conId: string; body: DraftEmailRequest }) =>
      apiRequest<DraftEmailResponse>(
        `/v1/workspaces/${wsId()}/contacts/${args.conId}:draft_email`,
        { method: "POST", body: args.body },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent-actions", wsId()] }),
  }));
}

export function useGenerateContactBrief(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { conId: string }) =>
      apiRequest<BriefResponse>(`/v1/workspaces/${wsId()}/contacts/${args.conId}:generate_brief`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent-actions", wsId()] }),
  }));
}

export function useGenerateDealBrief(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { dealId: string }) =>
      apiRequest<BriefResponse>(`/v1/workspaces/${wsId()}/deals/${args.dealId}:generate_brief`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent-actions", wsId()] }),
  }));
}

export function useAgents(wsId: () => string) {
  return useQuery(() => ({
    queryKey: ["agents", wsId()],
    queryFn: () => apiRequest<AgentList>(`/v1/workspaces/${wsId()}/agents`),
    staleTime: 60_000,
  }));
}

export function useInvokeBuiltinAgent(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { builtinKind: string }) =>
      apiRequest<InvokeBuiltinResponse>(
        `/v1/workspaces/${wsId()}/agents/builtin/${args.builtinKind}:invoke`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agents", wsId()] });
      qc.invalidateQueries({ queryKey: ["agent-actions", wsId()] });
    },
  }));
}

export function useApproveAgentAction(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { actionId: string }) =>
      apiRequest<AgentActionRead>(
        `/v1/workspaces/${wsId()}/agent-actions/${args.actionId}:approve`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent-actions", wsId()] });
      qc.invalidateQueries({ queryKey: ["accounts", wsId()] });
    },
  }));
}

export function useBulkApproveAgentActions(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { actionIds: string[] }) =>
      apiRequest<BulkApproveResponse>(`/v1/workspaces/${wsId()}/agent-actions:bulk_approve`, {
        method: "POST",
        body: { action_ids: args.actionIds },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent-actions", wsId()] });
      qc.invalidateQueries({ queryKey: ["accounts", wsId()] });
      qc.invalidateQueries({ queryKey: ["contacts", wsId()] });
    },
  }));
}

export function useRejectAgentAction(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { actionId: string; reason?: string | null }) =>
      apiRequest<AgentActionRead>(
        `/v1/workspaces/${wsId()}/agent-actions/${args.actionId}:reject`,
        { method: "POST", body: { reason: args.reason ?? null } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent-actions", wsId()] }),
  }));
}

export function useUpdateAgent(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { agentId: string; body: AgentUpdate; version: number }) =>
      apiRequest<AgentRead>(`/v1/workspaces/${wsId()}/agents/${args.agentId}`, {
        method: "PATCH",
        body: args.body,
        ifMatch: args.version,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agents", wsId()] }),
  }));
}

export function useAgentActions(wsId: () => string, params: () => AgentActionParams) {
  return useQuery(() => ({
    queryKey: ["agent-actions", wsId(), params()],
    queryFn: () =>
      apiRequest<AgentActionPage>(`/v1/workspaces/${wsId()}/agent-actions`, { query: params() }),
  }));
}

// ── Reports ─────────────────────────────────────────────────────────────────

import type {
  DealsByOwnerReport,
  LeadScoreDistributionReport,
  PipelineByStageReport,
} from "./types";

export function usePipelineByStage(wsId: () => string, pipelineId: () => string | undefined) {
  return useQuery(() => ({
    queryKey: ["report-pipeline-by-stage", wsId(), pipelineId()],
    queryFn: () =>
      apiRequest<PipelineByStageReport>(`/v1/workspaces/${wsId()}/reports/pipeline_by_stage`, {
        query: { pipeline_id: pipelineId() },
      }),
    enabled: !!pipelineId(),
  }));
}

export function useDealsByOwner(wsId: () => string) {
  return useQuery(() => ({
    queryKey: ["report-deals-by-owner", wsId()],
    queryFn: () =>
      apiRequest<DealsByOwnerReport>(`/v1/workspaces/${wsId()}/reports/deals_by_owner`),
  }));
}

export function useLeadScoreDistribution(wsId: () => string) {
  return useQuery(() => ({
    queryKey: ["report-lead-score-distribution", wsId()],
    queryFn: () =>
      apiRequest<LeadScoreDistributionReport>(
        `/v1/workspaces/${wsId()}/reports/lead_score_distribution`,
      ),
  }));
}

// ── Notifications ───────────────────────────────────────────────────────────

import type {
  NotificationListPage,
  NotificationPreferenceRead,
  NotificationPreferenceUpdate,
  NotificationRead,
} from "./types";

export function useNotifications(
  wsId: () => string,
  opts?: () => { limit?: number; unread_only?: boolean },
) {
  return useQuery(() => ({
    queryKey: ["notifications", wsId(), opts?.() ?? {}],
    queryFn: () =>
      apiRequest<NotificationListPage>(`/v1/workspaces/${wsId()}/notifications`, {
        query: opts?.() ?? {},
      }),
    // Poll every 60s so the bell badge stays roughly current without
    // a websocket. Cheap query; an admin in long sessions can tab over.
    refetchInterval: 60_000,
  }));
}

export function useMarkNotificationRead(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (args: { id: string }) =>
      apiRequest<NotificationRead>(`/v1/workspaces/${wsId()}/notifications/${args.id}:mark_read`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications", wsId()] }),
  }));
}

export function useMarkAllNotificationsRead(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: () =>
      apiRequest<{ marked_read: number }>(`/v1/workspaces/${wsId()}/notifications:mark_all_read`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications", wsId()] }),
  }));
}

export function useNotificationPreferences(wsId: () => string) {
  return useQuery(() => ({
    queryKey: ["notification-preferences", wsId()],
    queryFn: () =>
      apiRequest<NotificationPreferenceRead>(`/v1/workspaces/${wsId()}/notification-preferences`),
  }));
}

export function useUpdateNotificationPreferences(wsId: () => string) {
  const qc = useQueryClient();
  return useMutation(() => ({
    mutationFn: (body: NotificationPreferenceUpdate) =>
      apiRequest<NotificationPreferenceRead>(`/v1/workspaces/${wsId()}/notification-preferences`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notification-preferences", wsId()] }),
  }));
}
